import { useAuthStore } from '@/stores/auth-store';
import type { QueryParams, ValidationDetails } from '@/types/api';
import { ApiError, NETWORK_ERROR_MESSAGE } from './api-error';
import { env } from './env';
import { handleUnauthorized } from './session';

/**
 * Cliente HTTP centralizado.
 *
 * Responsabilidades (una sola vez, para todo el frontend):
 *  1. Prefijar la URL del backend (`VITE_API_URL`, ver `src/lib/env.ts`).
 *  2. Agregar el JWT de la sesión activa en `Authorization: Bearer <token>`.
 *  3. Desenvolver el envelope real de la API: devuelve `data` o lanza `ApiError`
 *     con el status y el `message` en español que mandó el backend.
 *  4. Ante 401: avisar al handler global (limpia la sesión y la caché → las rutas
 *     protegidas redirigen solas a /login).
 *
 * Las pantallas NO deberían usar `fetch` directo: usan este cliente a través de
 * `src/api/*.ts` + TanStack Query.
 */

/** Prefijo de todos los endpoints del backend (ver docs/openapi.yaml). */
export const API_PREFIX = '/api';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  /** Body JSON (objeto). Usa `formData` para subidas multipart. */
  body?: unknown;
  /** Subida multipart (ej. logo del tenant). */
  formData?: FormData;
  /** Query string; se omiten los valores `undefined` y `null`. */
  query?: QueryParams;
  /** `false` para endpoints públicos: `POST /api/auth/login`, `/register`, `/health`, webhooks. */
  auth?: boolean;
  /**
   * Qué se espera del cuerpo de la respuesta.
   *
   * `'json'` (default): el envelope de la API, y `apiRequest` devuelve su `data`.
   * `'blob'`: una DESCARGA (hoy, el CSV del CRM). El backend manda el archivo crudo y aquí se
   * devuelve la `Response` sin tocar, para que el llamador haga `.blob()` y lea sus cabeceras
   * (`Content-Type`, `Content-Disposition`, `X-Export-*`). Los errores se siguen lanzando como
   * `ApiError`, porque un error del backend siempre es JSON con envelope.
   */
  responseType?: 'json' | 'blob';
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** Construye la URL absoluta con query string. */
function buildUrl(path: string, query?: QueryParams): string {
  const base = path.startsWith('http') ? path : `${env.apiUrl}${path.startsWith('/') ? path : `/${path}`}`;

  if (!query) return base;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    search.append(key, String(value));
  }

  const queryString = search.toString();
  return queryString ? `${base}?${queryString}` : base;
}

/** Token de la sesión activa (null si no hay sesión). */
function currentToken(): string | null {
  return useAuthStore.getState().token;
}

/**
 * Ejecuta una petición y devuelve `data` (el contenido del envelope de éxito).
 * Lanza `ApiError` con cualquier respuesta de error del backend.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    formData,
    query,
    auth = true,
    responseType = 'json',
    headers = {},
    signal,
  } = options;

  const requestHeaders: Record<string, string> = {
    // Una descarga no pide el envelope: se declara lo que se espera recibir (el backend no lo usa,
    // pero deja el request honesto y sirve si algún día hay un proxy/caché en medio).
    Accept: responseType === 'blob' ? 'text/csv, */*' : 'application/json',
    ...headers,
  };

  let payload: BodyInit | undefined;

  if (formData) {
    payload = formData; // El navegador pone el Content-Type con el boundary correcto.
  } else if (body !== undefined) {
    requestHeaders['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  if (auth) {
    const token = currentToken();
    if (token) requestHeaders.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers: requestHeaders,
      body: payload,
      signal,
    });
  } catch (error) {
    // Cancelaciones (AbortController) se propagan tal cual: TanStack Query las ignora.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, NETWORK_ERROR_MESSAGE, undefined, error);
  }

  if (responseType === 'blob') {
    await assertBlobResponse(response);
    // Devuelve la `Response` CRUDA: el llamador hace `.blob()` y lee sus cabeceras. Por eso el tipo
    // genérico en este camino es `Response`, no el tipo del envelope.
    return response as unknown as T;
  }

  return parseResponse<T>(response);
}

/**
 * Respuesta binaria (`responseType: 'blob'`): un archivo, no un JSON con envelope.
 *
 * Si la respuesta es de error, el backend sigue mandando el envelope JSON, así que se lee el texto y
 * se lanza el MISMO `ApiError` (con el mensaje en español que escribió el backend) para que la
 * pantalla lo muestre con `getErrorMessage` como en cualquier otra llamada.
 */
async function assertBlobResponse(response: Response): Promise<void> {
  if (response.status === 401) {
    // Un 401 limpia la sesión: la ruta protegida redirige a /login por su cuenta.
    handleUnauthorized();
  }

  if (response.ok) return;

  const rawBody = await response.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawBody) as unknown;
  } catch {
    parsed = undefined;
  }

  const envelope =
    typeof parsed === 'object' && parsed !== null && 'success' in parsed
      ? (parsed as { error?: { message?: string; details?: ValidationDetails } })
      : null;

  throw new ApiError(
    response.status,
    envelope?.error?.message ?? `Error ${response.status} ${response.statusText}`.trim(),
    envelope?.error?.details,
  );
}

/** Lee el body (JSON o texto) y lo convierte a `data` o a `ApiError`. */
async function parseResponse<T>(response: Response): Promise<T> {
  const rawBody = await response.text();
  const hasBody = rawBody.trim().length > 0;

  let parsed: unknown;
  if (hasBody) {
    try {
      parsed = JSON.parse(rawBody) as unknown;
    } catch {
      parsed = undefined;
    }
  }

  if (response.status === 401) {
    // Un 401 limpia la sesión: la ruta protegida redirige a /login por su cuenta.
    handleUnauthorized();
  }

  if (typeof parsed === 'object' && parsed !== null && 'success' in parsed) {
    const envelope = parsed as
      | { success: true; data?: T }
      | { success: false; error?: { message?: string; details?: ValidationDetails } };

    if (envelope.success) {
      return (envelope as { data?: T }).data as T;
    }

    const message =
      envelope.error?.message ?? `La petición falló con estado ${response.status}.`;
    throw new ApiError(response.status, message, envelope.error?.details);
  }

  // Respuesta sin envelope (HTML de un proxy, 404 del servidor de archivos, etc.).
  if (!response.ok) {
    throw new ApiError(response.status, `Error ${response.status} ${response.statusText}`.trim());
  }

  throw new ApiError(
    response.status,
    'La respuesta del servidor no tiene el formato esperado ({ success, data }).',
  );
}

/** Atajos por método HTTP. */
export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};
